(() => {
  const q = (selector, scope = document) => scope.querySelector(selector);
  const qa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  const currency = window.throwbackTheme?.currency || 'USD';
  const money = cents => new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((Number(cents) || 0) / 100);

  const cartDrawer = q('[data-cart-drawer]');
  const cartOverlay = q('[data-cart-overlay]');
  const cartBody = q('[data-cart-body]');
  const cartCountEls = () => qa('[data-cart-count]');
  let cartReturnFocus = null;
  const cartError = () => q('[data-cart-error]', cartDrawer);
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const mobileNavMedia = window.matchMedia('(max-width: 780px)');
  let navReturnFocus = null;

  function closeNavDropdowns(exceptItem = null) {
    qa('[data-nav-item].is-open').forEach(item => {
      if (item === exceptItem) return;
      item.classList.remove('is-open');
      q('[data-nav-toggle]', item)?.setAttribute('aria-expanded', 'false');
    });
  }

  function setMobileMenu(open, { restoreFocus = true } = {}) {
    const menu = q('[data-mobile-menu]');
    const toggle = q('[data-mobile-menu-toggle]');
    if (!menu || !toggle) return;

    if (!mobileNavMedia.matches) {
      menu.classList.remove('is-open');
      menu.removeAttribute('inert');
      menu.removeAttribute('aria-hidden');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
      document.body.classList.remove('nav-open');
      return;
    }

    if (open) {
      navReturnFocus = document.activeElement;
      menu.classList.add('is-open');
      menu.removeAttribute('inert');
      menu.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close menu');
      document.body.classList.add('nav-open');
      window.requestAnimationFrame(() => q('[data-mobile-menu-close]', menu)?.focus());
      return;
    }

    menu.classList.remove('is-open');
    menu.setAttribute('inert', '');
    menu.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open menu');
    document.body.classList.remove('nav-open');
    closeNavDropdowns();
    if (restoreFocus && navReturnFocus && typeof navReturnFocus.focus === 'function') navReturnFocus.focus();
    navReturnFocus = null;
  }
  function showCartError(message) { const el = cartError(); if (!el) return; el.textContent = message || 'Something went wrong. Please try again.'; el.hidden = false; }
  function clearActionError(trigger) { const host = trigger?.closest('form[data-product-form]') || trigger?.closest('[data-product-root], .tb-product-card, .tb-addon, .tb-bundle, .cart-addon') || trigger?.parentElement; const el = host ? q('.tb-action-error', host) : null; if (el) el.textContent = ''; }
  function showActionError(trigger, message) {
    const productForm = trigger?.closest('form[data-product-form]');
    const host = productForm || trigger?.closest('[data-product-root], .tb-product-card, .tb-addon, .tb-bundle, .cart-addon') || trigger?.parentElement;
    if (!host) return;
    let el = q('.tb-action-error', host);
    if (!el) {
      el = document.createElement('div');
      el.className = 'tb-action-error';
      el.setAttribute('role', 'status');
      const buyRow = productForm ? q('.tb-buy-row', productForm) : null;
      if (buyRow) buyRow.insertAdjacentElement('afterend', el); else host.appendChild(el);
    }
    el.textContent = message || 'Something went wrong. Please try again.';
  }
  function clearCartError() { const el = cartError(); if (el) { el.textContent = ''; el.hidden = true; } }

  function setGalleryMedia(gallery, { mediaType = 'image', src = '', srcset = '', fullSrc = '', alt = '' } = {}) {
    if (!gallery) return;
    const main = q('[data-gallery-main]', gallery);
    const media = q('[data-gallery-media]', gallery);
    const zoomButton = q('[data-gallery-zoom-button]', gallery);
    if (!main || !media) return;

    if (mediaType === 'video') {
      media.innerHTML = `<video controls playsinline autoplay muted src="${src}"></video>`;
      media.classList.remove('is-zoomable');
      media.removeAttribute('data-gallery-zoom');
      media.removeAttribute('tabindex');
      media.removeAttribute('role');
      media.removeAttribute('aria-label');
      main.removeAttribute('data-lightbox-src');
      if (zoomButton) zoomButton.hidden = true;
      return;
    }

    media.innerHTML = srcset
      ? `<img src="${src}" srcset="${escapeHtml(srcset)}" sizes="(min-width: 1025px) 55vw, 100vw" alt="${escapeHtml(alt)}">`
      : `<img src="${src}" alt="${escapeHtml(alt)}">`;
    media.classList.add('is-zoomable');
    media.setAttribute('data-gallery-zoom', '');
    media.setAttribute('tabindex', '0');
    media.setAttribute('role', 'button');
    media.setAttribute('aria-label', 'Enlarge product image');
    if (fullSrc || src) main.dataset.lightboxSrc = fullSrc || src;
    if (zoomButton) zoomButton.hidden = false;
  }

  function setProductLightboxZoom(dialog, zoomed, { xRatio = 0.5, yRatio = 0.5 } = {}) {
    if (!dialog) return;
    const stage = q('[data-lightbox-stage]', dialog);
    const imageEl = q('[data-lightbox-image]', dialog);
    if (!stage || !imageEl) return;

    stage.classList.toggle('is-zoomed', zoomed);
    imageEl.setAttribute('aria-label', zoomed ? 'Fit product image to screen' : 'Zoom in on product image');

    window.requestAnimationFrame(() => {
      if (!zoomed) {
        stage.scrollTo({ left: 0, top: 0, behavior: 'auto' });
        return;
      }

      const maxLeft = Math.max(0, stage.scrollWidth - stage.clientWidth);
      const maxTop = Math.max(0, stage.scrollHeight - stage.clientHeight);
      stage.scrollTo({
        left: maxLeft * Math.min(1, Math.max(0, xRatio)),
        top: maxTop * Math.min(1, Math.max(0, yRatio)),
        behavior: 'auto'
      });
    });
  }

  function toggleProductLightboxZoom(dialog, event) {
    if (!dialog) return;
    const stage = q('[data-lightbox-stage]', dialog);
    const imageEl = q('[data-lightbox-image]', dialog);
    if (!stage || !imageEl) return;

    if (stage.classList.contains('is-zoomed')) {
      setProductLightboxZoom(dialog, false);
      return;
    }

    let xRatio = 0.5;
    let yRatio = 0.5;
    if (event && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
      const rect = imageEl.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        xRatio = (event.clientX - rect.left) / rect.width;
        yRatio = (event.clientY - rect.top) / rect.height;
      }
    }
    setProductLightboxZoom(dialog, true, { xRatio, yRatio });
  }

  function openProductLightbox(gallery) {
    if (!gallery) return;
    const main = q('[data-gallery-main]', gallery);
    const dialog = q('[data-product-lightbox]', gallery);
    const imageEl = q('[data-lightbox-image]', dialog);
    const sourceImage = q('[data-gallery-media] img', gallery);
    const src = main?.dataset.lightboxSrc || sourceImage?.currentSrc || sourceImage?.src;
    if (!dialog || !imageEl || !src) return;

    imageEl.src = src;
    imageEl.alt = sourceImage?.alt || '';
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');

    const applyInitialZoom = () => {
      setProductLightboxZoom(dialog, true);
      q('[data-lightbox-close]', dialog)?.focus();
    };
    if (imageEl.complete) window.requestAnimationFrame(applyInitialZoom);
    else imageEl.addEventListener('load', applyInitialZoom, { once: true });
  }

  function closeProductLightbox(dialog) {
    if (!dialog) return;
    setProductLightboxZoom(dialog, false);
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    const imageEl = q('[data-lightbox-image]', dialog);
    if (imageEl) imageEl.removeAttribute('src');
  }

  function openCart() {
    if (!cartDrawer) return;
    cartReturnFocus = document.activeElement;
    clearCartError();
    cartDrawer.removeAttribute('inert');
    cartDrawer.setAttribute('aria-hidden', 'false');
    cartDrawer.classList.add('is-open');
    cartOverlay?.classList.add('is-open');
    document.body.classList.add('drawer-open');
    q('[data-cart-close]', cartDrawer)?.focus();
  }

  function closeCart() {
    cartDrawer?.classList.remove('is-open');
    cartOverlay?.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
    if (cartDrawer) { cartDrawer.setAttribute('inert', ''); window.setTimeout(() => { if (!cartDrawer.classList.contains('is-open')) cartDrawer.setAttribute('aria-hidden', 'true'); }, 300); }
    if (cartReturnFocus && typeof cartReturnFocus.focus === 'function') cartReturnFocus.focus();
  }

  async function getCart() {
    const response = await fetch(`${window.throwbackTheme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  async function refreshCartServerContent() {
    const currentAddons = q('[data-cart-addons]', cartDrawer);
    const currentProgress = q('[data-free-shipping-progress]', cartDrawer);
    const currentNudge = q('[data-tacklebox-nudge]', cartDrawer);
    const currentPill = q('[data-cart-pill]');
    if (!currentAddons && !currentProgress && !currentNudge && !currentPill) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', 'cart-drawer');
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const nextAddons = q('[data-cart-addons]', doc);
      if (currentAddons && nextAddons) currentAddons.replaceWith(nextAddons);

      const nextProgress = q('[data-free-shipping-progress]', doc);
      if (currentProgress && nextProgress) currentProgress.replaceWith(nextProgress);

      const nextNudge = q('[data-tacklebox-nudge]', doc);
      if (currentNudge && nextNudge) currentNudge.replaceWith(nextNudge);

      const nextPill = q('[data-cart-pill]', doc);
      if (currentPill && nextPill) currentPill.replaceWith(nextPill);
    } catch (_) {
      // Cart remains fully usable if server-rendered enhancements fail to refresh.
    }
  }

  function escapeHtml(value = '') {
    return String(value).replace(/[&<>'"]/g, char => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' })[char]);
  }

  function renderCart(cart) {
    cartCountEls().forEach(el => { el.textContent = cart.item_count; });
    const countLabel = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
    qa('[data-cart-open]').forEach(el => el.setAttribute('aria-label', `Open cart, ${countLabel}`));
    qa('.cart-drawer__count').forEach(el => el.setAttribute('aria-label', countLabel));
    const subtotal = q('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.items_subtotal_price);
    const savings = q('[data-cart-savings]');
    const savingsAmount = q('[data-cart-savings-amount]');
    if (savings) savings.hidden = !(cart.total_discount > 0);
    if (savingsAmount) savingsAmount.textContent = `−${money(cart.total_discount || 0)}`;

    if (!cartBody) return;
    if (!cart.items.length) {
      cartBody.innerHTML = `<div class="cart-empty"><h3 class="tb-heading tb-heading--small">Your cart’s empty</h3><p class="tb-copy">Every trophy starts with one cast.</p><a class="tb-button tb-button--primary" href="${escapeHtml(window.throwbackTheme.routes.allProducts || '/collections/all')}">Shop the baits</a></div>`;
      return;
    }

    cartBody.innerHTML = cart.items.map(item => `
      <div class="cart-line" data-cart-line="${escapeHtml(item.key)}">
        ${item.image ? `<img class="cart-line__image" src="${escapeHtml(item.image)}${String(item.image).includes('?') ? '&' : '?'}width=160" alt="${escapeHtml(item.product_title)}">` : '<div class="cart-line__image"></div>'}
        <div>
          <div class="cart-line__top">
            <a class="cart-line__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            <button class="cart-line__remove" type="button" data-cart-remove data-line-key="${escapeHtml(item.key)}" aria-label="Remove ${escapeHtml(item.product_title)}">Remove</button>
          </div>
          <div class="cart-line__variant">${item.variant_title && item.variant_title !== 'Default Title' ? escapeHtml(item.variant_title) : ''}${item.selling_plan_allocation?.selling_plan?.name ? `<span class="cart-line__plan">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</span>` : ''}</div>
          ${(item.line_level_discount_allocations || []).filter(a => a.amount > 0).map(a => `<div class="cart-line__discount">${escapeHtml(a.discount_application?.title || 'Discount')} <span>−${money(a.amount)}</span></div>`).join('')}
          <div class="cart-line__bottom">
            <div class="tb-qty">
              <button type="button" data-cart-change="-1" data-line-key="${escapeHtml(item.key)}" aria-label="Decrease quantity of ${escapeHtml(item.product_title)}">−</button>
              <input type="number" value="${item.quantity}" min="0" readonly aria-label="Quantity of ${escapeHtml(item.product_title)}">
              <button type="button" data-cart-change="1" data-line-key="${escapeHtml(item.key)}" aria-label="Increase quantity of ${escapeHtml(item.product_title)}">+</button>
            </div>
            <span class="cart-line__price">${item.original_line_price > item.final_line_price ? `<s>${money(item.original_line_price)}</s> ` : ''}<strong>${money(item.final_line_price)}</strong></span>
          </div>
        </div>
      </div>`).join('') + '<div class="cart-drawer__clear-row"><button class="cart-drawer__clear" type="button" data-cart-clear>Clear cart</button></div>';
  }

  async function refreshCart({ open = false } = {}) {
    const cart = await getCart();
    renderCart(cart);
    await refreshCartServerContent();
    if (open) openCart();
    window.dispatchEvent(new CustomEvent('throwback:cart:changed', { detail: { cart } }));
    return cart;
  }

  window.addEventListener('throwback:cart:refresh', event => {
    refreshCart({ open: event.detail?.open !== false }).catch(error => showCartError(error.message));
  });

  // Hero background loop: start after the page has loaded, pick the smallest MP4
  // that covers the screen, and skip it for reduced motion or data saver.
  function startHeroVideo() {
    const video = q('[data-hero-video]');
    if (!video || video.dataset.started) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
    let sources = [];
    try { sources = JSON.parse(video.dataset.sources || '[]'); } catch (_) { return; }
    if (!sources.length) return;
    const need = Math.min(window.innerWidth * Math.min(window.devicePixelRatio || 1, 2), window.innerWidth < 780 ? 1280 : 1920);
    sources.sort((a, b) => a.w - b.w);
    const pick = sources.find(s => s.w >= need) || sources[sources.length - 1];
    video.dataset.started = '1';
    video.src = pick.url;
    video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
    video.play().catch(() => {});
  }
  const queueHeroVideo = () => ('requestIdleCallback' in window ? window.requestIdleCallback(startHeroVideo, { timeout: 2500 }) : window.setTimeout(startHeroVideo, 1200));
  if (document.readyState === 'complete') queueHeroVideo(); else window.addEventListener('load', queueHeroVideo, { once: true });

  // Floating cart pill (see sections/cart-drawer.liquid).
  const PILL_KEY = 'tb-cart-pill';
  function showCartPill() {
    document.documentElement.classList.add('has-cart-pill');
    try { window.sessionStorage.setItem(PILL_KEY, '1'); } catch (_) {}
    const pill = q('[data-cart-pill]');
    if (!pill) return;
    pill.classList.remove('is-bumped');
    void pill.offsetWidth;
    pill.classList.add('is-bumped');
  }
  try { if (window.sessionStorage.getItem(PILL_KEY) === '1') document.documentElement.classList.add('has-cart-pill'); } catch (_) {}

  function markQuickAdded(button) {
    const label = q('.tb-product-card__cta-label', button);
    const glyph = q('.tb-product-card__cta-mobile', button);
    const target = label || button;
    if (!button.dataset.addedOriginal) button.dataset.addedOriginal = target.textContent;
    if (glyph && !button.dataset.addedGlyph) button.dataset.addedGlyph = glyph.textContent;
    target.textContent = label ? 'Added ✓' : 'Added';
    if (glyph) glyph.textContent = '✓';
    button.classList.add('is-added');
    window.clearTimeout(Number(button.dataset.addedTimer || 0));
    button.dataset.addedTimer = String(window.setTimeout(() => {
      target.textContent = button.dataset.addedOriginal;
      if (glyph) glyph.textContent = button.dataset.addedGlyph;
      button.classList.remove('is-added');
    }, 1600));
  }

  // Hidden attribution for analytics. Lines added by a theme feature carry
  // `_source` (e.g. "cart-addon-gap@collection"). Leading-underscore properties
  // are hidden from shoppers in cart and checkout but show on the order in admin.
  function pageLabel() {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/') return 'home';
    if (path.includes('/pages/lt-tacklebox')) return 'tacklebox';
    if (path.includes('/collections/')) return path.includes('/products/') ? 'product' : 'collection';
    if (path.includes('/products/')) return 'product';
    if (path.includes('/pages/')) return 'page';
    if (path.includes('/search')) return 'search';
    if (path.includes('/cart')) return 'cart';
    return 'other';
  }

  function sourceFor(trigger, fallback) {
    let kind = trigger?.dataset?.source || fallback;
    if (!kind && trigger) {
      if (trigger.closest('.cart-addon')) kind = 'cart-addon';
      else if (trigger.closest('.tb-addon')) kind = 'pdp-addon';
      else if (trigger.closest('.tb-product-card')) kind = 'quick-add';
    }
    return kind ? `${kind}@${pageLabel()}` : '';
  }

  function withSource(items, source) {
    if (!source) return items;
    return items.map(item => ({ ...item, properties: { ...(item.properties || {}), _source: source } }));
  }

  window.throwbackTheme = window.throwbackTheme || {};
  window.throwbackTheme.sourceFor = sourceFor;
  window.throwbackTheme.withSource = withSource;

  async function addItems(items, { open = true } = {}) {
    const response = await fetch(window.throwbackTheme.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'Unable to add item');
    }
    await refreshCart({ open });
  }

  async function changeLine(key, quantity) {
    const response = await fetch(window.throwbackTheme.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Unable to update cart');
    }
    clearCartError();
    const cart = await response.json();
    renderCart(cart);
    await refreshCartServerContent();
  }

  // One request per cart line at a time. Buttons stay disabled until the cart
  // re-renders, so rapid taps can't send stale quantities or race each other.
  const pendingLines = new Set();
  async function updateLineLocked(line, key, quantity) {
    if (!key || pendingLines.has(key)) return;
    pendingLines.add(key);
    const buttons = line ? qa('button', line) : [];
    buttons.forEach(button => { button.disabled = true; });
    if (line) line.setAttribute('aria-busy', 'true');
    try { await changeLine(key, quantity); }
    catch (error) { showCartError(error.message); }
    finally {
      pendingLines.delete(key);
      if (line && line.isConnected) {
        buttons.forEach(button => { button.disabled = false; });
        line.removeAttribute('aria-busy');
      }
    }
  }

  document.addEventListener('keydown', event => {
    const zoomTarget = event.target.closest?.('[data-gallery-zoom]');
    if (zoomTarget && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      openProductLightbox(zoomTarget.closest('[data-product-gallery]'));
      return;
    }

    const lightboxZoomTarget = event.target.closest?.('[data-lightbox-toggle]');
    if (lightboxZoomTarget && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      toggleProductLightboxZoom(lightboxZoomTarget.closest('[data-product-lightbox]'));
      return;
    }

    const openLightbox = q('[data-product-lightbox][open]');
    if (openLightbox && event.key === 'Escape') {
      event.preventDefault();
      closeProductLightbox(openLightbox);
      return;
    }
  });

  document.addEventListener('keydown', event => {
    if (!cartDrawer?.classList.contains('is-open')) return;
    if (event.key === 'Escape') { event.preventDefault(); closeCart(); return; }
    if (event.key !== 'Tab') return;
    const focusable = qa(focusableSelector, cartDrawer).filter(el => !el.hasAttribute('hidden'));
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  document.addEventListener('keydown', event => {
    const menu = q('[data-mobile-menu]');
    if (!mobileNavMedia.matches || !menu?.classList.contains('is-open') || event.key !== 'Tab') return;
    const focusable = qa(focusableSelector, menu).filter(el => !el.hasAttribute('hidden'));
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const openNavItem = q('[data-nav-item].is-open');
    if (openNavItem) {
      const toggle = q('[data-nav-toggle]', openNavItem);
      closeNavDropdowns();
      toggle?.focus();
      return;
    }
    const mobileMenu = q('[data-mobile-menu]');
    if (mobileMenu?.classList.contains('is-open')) {
      const toggle = q('[data-mobile-menu-toggle]');
      setMobileMenu(false);
      toggle?.focus();
    }
  });

  document.addEventListener('click', async event => {
    const open = event.target.closest('[data-cart-open]');
    if (open) { event.preventDefault(); openCart(); return; }
    if (event.target.closest('[data-cart-close]') || event.target === cartOverlay) { closeCart(); return; }

    const quickAdd = event.target.closest('[data-quick-add]');
    if (quickAdd) {
      event.preventDefault();
      const id = Number(quickAdd.dataset.variantId);
      if (!id) return;
      // Inside the cart drawer the drawer is already open; everywhere else
      // (product grids, product page add-ons) keep the shopper browsing.
      const fromDrawer = Boolean(quickAdd.closest('[data-cart-drawer]'));
      quickAdd.disabled = true;
      try {
        clearActionError(quickAdd);
        await addItems(withSource([{ id, quantity: 1 }], sourceFor(quickAdd)), { open: fromDrawer });
        if (!fromDrawer) { markQuickAdded(quickAdd); showCartPill(); }
      }
      catch (error) { showActionError(quickAdd, error.message); }
      finally { quickAdd.disabled = false; }
      return;
    }

    const bundle = event.target.closest('[data-add-bundle]');
    if (bundle) {
      event.preventDefault();
      const ids = JSON.parse(bundle.dataset.variantIds || '[]').map(Number).filter(Boolean);
      if (bundle.dataset.includeCurrentProduct === 'true') {
        const productForm = q('[data-type="add-to-cart-form"]');
        const currentId = Number(q('[name="id"]', productForm)?.value);
        const currentBuyable = !q('[data-product-submit]', productForm)?.disabled;
        if (currentId && currentBuyable) ids.unshift(currentId);
      }
      if (!ids.length) return;
      bundle.disabled = true;
      try { clearActionError(bundle); await addItems(withSource(ids.map(id => ({ id, quantity: 1 })), sourceFor(bundle, 'complete-setup'))); }
      catch (error) { showActionError(bundle, error.message); }
      finally { bundle.disabled = false; }
      return;
    }

    const videoPlay = event.target.closest('[data-video-embed] .tb-video-embed__play');
    if (videoPlay) {
      const embed = videoPlay.closest('[data-video-embed]');
      const iframe = document.createElement('iframe');
      iframe.src = embed.dataset.embedSrc;
      iframe.title = embed.dataset.embedTitle || 'Video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      embed.replaceChildren(iframe);
      iframe.focus();
      return;
    }

    const cartChange = event.target.closest('[data-cart-change]');
    if (cartChange) {
      const line = cartChange.closest('[data-cart-line]');
      const current = Number(q('input', line)?.value || 0);
      await updateLineLocked(line, cartChange.dataset.lineKey, Math.max(0, current + Number(cartChange.dataset.cartChange)));
      return;
    }

    const clearCart = event.target.closest('[data-cart-clear]');
    if (clearCart) {
      // Two taps so a full cart is never wiped by accident.
      if (clearCart.dataset.confirm !== 'true') {
        clearCart.dataset.confirm = 'true';
        clearCart.textContent = 'Tap again to clear all items';
        setTimeout(() => {
          if (clearCart.isConnected) { delete clearCart.dataset.confirm; clearCart.textContent = 'Clear cart'; }
        }, 4000);
        return;
      }
      clearCart.disabled = true;
      clearCart.textContent = 'Clearing…';
      try {
        const response = await fetch(window.throwbackTheme.routes.cartClear || '/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Unable to clear the cart. Please try again.');
        await refreshCart({ open: true });
      } catch (error) {
        showCartError(error.message);
        clearCart.disabled = false;
        clearCart.textContent = 'Clear cart';
        delete clearCart.dataset.confirm;
      }
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      await updateLineLocked(remove.closest('[data-cart-line]'), remove.dataset.lineKey, 0);
      return;
    }

    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) {
      const gallery = thumb.closest('[data-product-gallery]');
      qa('[data-gallery-thumb]', gallery).forEach(el => el.classList.remove('is-active'));
      thumb.classList.add('is-active');
      setGalleryMedia(gallery, {
        mediaType: thumb.dataset.mediaType || 'image',
        src: thumb.dataset.src || '',
        srcset: thumb.dataset.srcset || '',
        fullSrc: thumb.dataset.lightboxSrc || thumb.dataset.src || '',
        alt: thumb.dataset.alt || ''
      });
      return;
    }

    const galleryZoom = event.target.closest('[data-gallery-zoom], [data-gallery-zoom-button]');
    if (galleryZoom) {
      const gallery = galleryZoom.closest('[data-product-gallery]');
      openProductLightbox(gallery);
      return;
    }

    const lightboxClose = event.target.closest('[data-lightbox-close]');
    if (lightboxClose) {
      closeProductLightbox(lightboxClose.closest('[data-product-lightbox]'));
      return;
    }

    const lightboxZoom = event.target.closest('[data-lightbox-toggle]');
    if (lightboxZoom) {
      toggleProductLightboxZoom(lightboxZoom.closest('[data-product-lightbox]'), event);
      return;
    }

    const lightbox = event.target.closest('[data-product-lightbox]');
    if (lightbox && event.target === lightbox) {
      closeProductLightbox(lightbox);
      return;
    }

    const qty = event.target.closest('[data-qty-change]');
    if (qty) {
      const wrapper = qty.closest('[data-quantity]');
      const input = q('input', wrapper);
      input.value = Math.max(1, Number(input.value || 1) + Number(qty.dataset.qtyChange));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const mobileToggle = event.target.closest('[data-mobile-menu-toggle]');
    if (mobileToggle) {
      const menu = q('[data-mobile-menu]');
      setMobileMenu(!menu?.classList.contains('is-open'));
      return;
    }

    if (event.target.closest('[data-mobile-menu-close]') || event.target.closest('[data-mobile-menu-overlay]')) {
      setMobileMenu(false);
      return;
    }

    const mobileMenuLink = event.target.closest('[data-mobile-menu] a[href]');
    if (mobileMenuLink && mobileNavMedia.matches) {
      setMobileMenu(false, { restoreFocus: false });
      return;
    }

    const navToggle = event.target.closest('[data-nav-toggle]');
    if (navToggle) {
      const item = navToggle.closest('[data-nav-item]');
      const willOpen = !item?.classList.contains('is-open');
      closeNavDropdowns(item);
      item?.classList.toggle('is-open', willOpen);
      navToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      return;
    }

    if (!event.target.closest('[data-site-header]')) closeNavDropdowns();
  });

  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-type="add-to-cart-form"]');
    if (!form) return;
    event.preventDefault();
    const button = q('[type="submit"]', form);
    button.disabled = true;
    ensureFormSource(form);
    const formData = new FormData(form);
    try {
      clearActionError(button);
      const response = await fetch(window.throwbackTheme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Unable to add item');
      }
      await refreshCart({ open: true });
    }
    catch (error) { showActionError(button, error.message); }
    finally { button.disabled = false; }
  });

  // Attribute product-page adds with a hidden field in the form itself, so
  // Buy it now / Shop Pay (which skip our submit handler) carry it too.
  // A shopper who came from a cart add-on ("Choose options") keeps that
  // add-on's source; everyone else is "pdp".
  function ensureFormSource(form) {
    if (!form || q('input[name="properties[_source]"]', form)) return;
    const fromAddon = new URLSearchParams(window.location.search).get('tb_src') || '';
    const kind = /^cart-addon[a-z-]{0,30}$/.test(fromAddon) ? `${fromAddon}-options` : 'pdp';
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_source]';
    input.value = `${kind}@${pageLabel()}`;
    form.appendChild(input);
  }

  function initProduct(root) {
    if (root.dataset.themeInitialized === 'true') return;
    root.dataset.themeInitialized = 'true';
    const variants = JSON.parse(q('[data-product-json]', root)?.textContent || '[]');
    const form = q('[data-product-form]', root);
    ensureFormSource(form);
    const idInput = q('[name="id"]', form);
    const price = q('[data-product-price]', root);
    const addButton = q('[data-product-submit]', root);
    const quantityInput = q('[name="quantity"]', form);
    const soldOutHook = q('[data-sold-out-hook]', root);
    const stockStatus = q('[data-product-stock]', root);
    const optionGroups = qa('[data-option-group]', root);
    let activeVariant = variants.find(item => Number(item.id) === Number(idInput?.value)) || variants[0] || null;
    let sellingPlans = {};
    try { sellingPlans = JSON.parse(q('[data-selling-plans]', root)?.textContent || '{}'); } catch (_) { sellingPlans = {}; }
    const hasPlans = Object.keys(sellingPlans).length > 0;
    function selectedPlan() {
      if (!hasPlans || !form) return null;
      const id = new FormData(form).get('selling_plan');
      return id ? sellingPlans[String(id)] || null : null;
    }
    function planSuffix(plan) {
      if (!plan) return '';
      if (/\byear/i.test(plan.name)) return ' / year';
      if (/\bmonth/i.test(plan.name)) return ' / month';
      return '';
    }

    function getSelectedOptions() {
      return optionGroups.map(group => q('input:checked', group)?.value);
    }

    function updateStockStatus(variant) {
      if (!stockStatus) return;
      if (!variant) {
        stockStatus.textContent = 'Unavailable';
        stockStatus.classList.add('is-sold-out');
        return;
      }
      stockStatus.textContent = variant.available ? 'In Stock' : 'Sold Out';
      stockStatus.classList.toggle('is-sold-out', !variant.available);
    }

    function updateOptionAvailability() {
      const selected = getSelectedOptions();

      optionGroups.forEach((group, optionIndex) => {
        qa('[data-option-input]', group).forEach(input => {
          const candidate = input.value;
          const hasAvailableVariant = variants.some(variant => {
            if (!variant.available || variant.options[optionIndex] !== candidate) return false;

            for (let previousIndex = 0; previousIndex < optionIndex; previousIndex += 1) {
              if (selected[previousIndex] && variant.options[previousIndex] !== selected[previousIndex]) return false;
            }
            return true;
          });

          const choice = input.closest('.tb-choice, .tb-swatch');
          choice?.classList.toggle('is-unavailable', !hasAvailableVariant);
          choice?.setAttribute('data-availability', hasAvailableVariant ? 'available' : 'unavailable');
        });
      });
    }

    function updateSubmitLabel() {
      if (!addButton) return;
      if (!activeVariant) {
        addButton.disabled = true;
        addButton.textContent = 'Unavailable';
        if (soldOutHook) {
          soldOutHook.hidden = false;
          soldOutHook.dataset.variantId = '';
        }
        updateStockStatus(null);
        return;
      }

      const quantity = Math.max(1, Number(quantityInput?.value || 1));
      const plan = selectedPlan();
      const unitPrice = plan ? plan.price : activeVariant.price;
      const suffix = planSuffix(plan);
      addButton.disabled = !activeVariant.available;
      // Only write when the text changes: the plan watcher observes this form.
      const nextLabel = activeVariant.available ? `Add to cart · ${money(unitPrice * quantity)}${suffix}` : 'Sold out';
      if (addButton.textContent !== nextLabel) addButton.textContent = nextLabel;
      const nextPrice = `${money(unitPrice)}${suffix}`;
      if (hasPlans && price && price.textContent !== nextPrice) price.textContent = nextPrice;
      if (soldOutHook) {
        soldOutHook.hidden = activeVariant.available;
        soldOutHook.dataset.variantId = String(activeVariant.id);
      }
      updateStockStatus(activeVariant);
    }

    function updateSelectedOptionLabels() {
      optionGroups.forEach(group => {
        const label = q('[data-selected-option-label]', group);
        const checked = q('input:checked', group);
        if (label && checked) label.textContent = checked.value;
      });
    }

    function selectVariant() {
      const selected = getSelectedOptions();
      const variant = variants.find(item => item.options.every((value, index) => value === selected[index])) || null;

      activeVariant = variant;
      updateSelectedOptionLabels();
      updateOptionAvailability();

      if (!variant) {
        updateSubmitLabel();
        return;
      }

      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      if (price) price.textContent = money(variant.price);
      updateSubmitLabel();
      qa('[data-bundle-total]').forEach(el => {
        const addonTotal = Number(el.dataset.addonTotal || 0);
        el.textContent = money(addonTotal + (variant.available ? variant.price : 0));
      });

      if (variant.featured_image?.src) {
        const baseSrc = String(variant.featured_image.src);
        const separator = baseSrc.includes('?') ? '&' : '?';
        setGalleryMedia(q('[data-product-gallery]', root), {
          mediaType: 'image',
          src: `${baseSrc}${separator}width=1400`,
          fullSrc: `${baseSrc}${separator}width=2400`,
          alt: variant.name || ''
        });
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }

    root.addEventListener('change', event => {
      if (event.target.matches('[data-option-input]')) selectVariant();
      if (event.target.matches('[name="quantity"]')) updateSubmitLabel();
    });

    // The subscription widget sets the plan inside the form; follow its changes.
    if (hasPlans && form) {
      let planTimer = 0;
      const syncPlan = () => { window.clearTimeout(planTimer); planTimer = window.setTimeout(updateSubmitLabel, 30); };
      root.addEventListener('click', syncPlan);
      root.addEventListener('change', syncPlan);
      new MutationObserver(syncPlan).observe(form, { subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'checked', 'name'] });
      window.setTimeout(updateSubmitLabel, 800);
      window.setTimeout(updateSubmitLabel, 2500);
    }

    updateSelectedOptionLabels();
    updateOptionAvailability();
    updateSubmitLabel();
  }
  qa('[data-product-root]').forEach(initProduct);

  function initCountdown(el) {
    if (el.dataset.themeInitialized === 'true') return;
    el.dataset.themeInitialized = 'true';
    const target = new Date(el.dataset.countdown).getTime();
    if (!Number.isFinite(target)) return;
    const cells = {
      days: q('[data-days]', el),
      hours: q('[data-hours]', el),
      minutes: q('[data-minutes]', el),
      seconds: q('[data-seconds]', el)
    };
    const tick = () => {
      const distance = Math.max(0, target - Date.now());
      const days = Math.floor(distance / 86400000);
      const hours = Math.floor((distance % 86400000) / 3600000);
      const minutes = Math.floor((distance % 3600000) / 60000);
      const seconds = Math.floor((distance % 60000) / 1000);
      if (cells.days) cells.days.textContent = String(days).padStart(2,'0');
      if (cells.hours) cells.hours.textContent = String(hours).padStart(2,'0');
      if (cells.minutes) cells.minutes.textContent = String(minutes).padStart(2,'0');
      if (cells.seconds) cells.seconds.textContent = String(seconds).padStart(2,'0');
      if (distance === 0) {
        const drop = el.closest('[data-drop]');
        const pre = drop ? q('[data-drop-pre]', drop) : null;
        const live = drop ? q('[data-drop-live]', drop) : null;
        if (pre && live) { pre.hidden = true; live.hidden = false; }
        if (el._countdownTimer) clearInterval(el._countdownTimer);
      }
    };
    tick();
    el._countdownTimer = setInterval(tick, 1000);
  }
  qa('[data-countdown]').forEach(initCountdown);

  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');

  function syncHeroMotion() {
    qa('.tb-hero__media video').forEach(video => {
      if (motionPreference.matches) {
        video.pause();
        video.removeAttribute('autoplay');
      } else {
        video.muted = true;
        video.setAttribute('autoplay', '');
        video.play().catch(() => {});
      }
    });
  }

  syncHeroMotion();
  document.addEventListener('shopify:section:unload', event => { event.target.querySelectorAll('[data-countdown]').forEach(el => { if (el._countdownTimer) clearInterval(el._countdownTimer); }); });
  document.addEventListener('shopify:section:load', event => {
    syncHeroMotion();
    event.target.querySelectorAll('[data-countdown]').forEach(el => initCountdown(el));
    event.target.querySelectorAll('[data-product-root]').forEach(root => initProduct(root));
  });
  const syncNavBreakpoint = event => {
    if (event.matches) setMobileMenu(false, { restoreFocus: false });
    else setMobileMenu(false, { restoreFocus: false });
  };
  setMobileMenu(false, { restoreFocus: false });
  if (mobileNavMedia.addEventListener) mobileNavMedia.addEventListener('change', syncNavBreakpoint);

  if (motionPreference.addEventListener) motionPreference.addEventListener('change', syncHeroMotion);
  else if (motionPreference.addListener) motionPreference.addListener(syncHeroMotion);

  /* Floating app launchers (rewards, chat, accessibility) sit on top of the mobile
     menu and cart drawer and cover their buttons. While either is open, hide any
     fixed-position element the apps inject at the top level of the page. Theme
     elements are never touched. */
  const OVERLAY_OPEN = ['nav-open', 'drawer-open'];
  const isThemeElement = (el) =>
    el.matches('script, style, link, noscript, template, header, main, footer, .skip-link, .shopify-section, [id^="shopify-section"], [data-mobile-menu], [data-cart-drawer], [data-cart-overlay]') ||
    el.querySelector('[data-mobile-menu], [data-cart-drawer]');
  let hiddenWidgets = [];
  function syncFloatingWidgets() {
    const open = OVERLAY_OPEN.some((c) => document.body.classList.contains(c));
    if (open && !hiddenWidgets.length) {
      hiddenWidgets = Array.from(document.body.children).filter((el) => {
        if (isThemeElement(el)) return false;
        // Apps often wrap the launcher, so check the element and two levels down.
        const fixed = (node) => ['fixed', 'sticky'].includes(window.getComputedStyle(node).position);
        return fixed(el) || Array.from(el.querySelectorAll(':scope > *, :scope > * > *')).some(fixed);
      });
      hiddenWidgets.forEach((el) => el.classList.add('tb-widget-hidden'));
    } else if (!open && hiddenWidgets.length) {
      hiddenWidgets.forEach((el) => el.classList.remove('tb-widget-hidden'));
      hiddenWidgets = [];
    }
  }
  new MutationObserver(syncFloatingWidgets).observe(document.body, { attributes: true, attributeFilter: ['class'] });

  /* Wholesale Gorilla's signup form (/pages/ws-account-create) arrives from the app
     with placeholders but no labels. Give every unlabelled field a visible label
     built from its placeholder, so the field name stays visible while typing. */
  function labelWholesaleFields(root) {
    let hasRequired = false;
    root.querySelectorAll('input, select, textarea').forEach((field) => {
      if (['hidden', 'submit', 'button', 'checkbox', 'radio'].includes(field.type)) return;
      const required = field.required || field.getAttribute('aria-required') === 'true';
      if (required) hasRequired = true;
      const id = field.id;
      let label = (id && root.querySelector(`label[for="${CSS.escape(id)}"]:not(.wsg-hidden-label)`)) || field.closest('label');
      if (!label && !field.dataset.tbLabelled) {
        let text = (field.getAttribute('placeholder') || field.getAttribute('aria-label') || '').trim();
        if (text.length > 40) text = field.tagName === 'TEXTAREA' ? 'About your shop' : ''; // long helper text stays as the placeholder
        if (text) {
          if (!id) field.id = `tb-wsg-${Math.random().toString(36).slice(2, 9)}`;
          label = document.createElement('label');
          label.htmlFor = field.id;
          label.className = 'tb-wsg-label';
          label.textContent = text;
          field.parentNode.insertBefore(label, field);
        }
      }
      field.dataset.tbLabelled = 'true';
      // Required marker follows the app's settings: change a field to required in
      // Wholesale Gorilla and the asterisk appears here automatically.
      if (label) {
        const marker = label.querySelector('.tb-wsg-req');
        if (required && !marker) {
          const star = document.createElement('span');
          star.className = 'tb-wsg-req';
          star.setAttribute('aria-hidden', 'true');
          star.textContent = '*';
          label.appendChild(star);
        } else if (!required && marker) {
          marker.remove();
        }
      }
    });
    // One "* Required" note at the top of the form
    const firstSection = root.querySelector('h2, h3');
    if (hasRequired && firstSection && !root.querySelector('.tb-wsg-legend')) {
      const legend = document.createElement('p');
      legend.className = 'tb-wsg-legend';
      legend.innerHTML = '<span class="tb-wsg-req" aria-hidden="true">*</span> Required';
      firstSection.insertAdjacentElement('afterend', legend);
    }
  }
  document.querySelectorAll('.wsg-proxy-container').forEach((container) => {
    labelWholesaleFields(container);
    new MutationObserver(() => labelWholesaleFields(container)).observe(container, { childList: true, subtree: true });
  });

})();
